import {
  CheckCircle2,
  GitBranch,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import type { SessionUser } from "../../../shared/auth/access";
import { OperatorHeaderMenu } from "../OperatorHeaderMenu";
import { useServerConnection } from "./useServerConnection";

export function OperatorTerminalHeader({
  label,
  hours,
  user,
  displayName,
  terminalName,
  terminalCode,
  operatingDay,
  dataStatus,
  machineControls,
  onLock,
  onActions,
  onLogout,
}: {
  label: string;
  hours: string;
  user: SessionUser;
  displayName?: string;
  terminalName?: string;
  terminalCode?: string;
  operatingDay?: {
    date: string;
    completedCount: number;
    onCompleted: () => void;
  };
  dataStatus?: "loading" | "unavailable";
  machineControls?: ReactNode;
  onLock?: () => Promise<void>;
  onActions?: () => void;
  onLogout: () => Promise<void>;
}) {
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const serverConnection = useServerConnection(!dataStatus);
  const connection = dataStatus
    ? dataStatus === "loading"
      ? "checking"
      : "offline"
    : serverConnection;
  const connectionText =
    dataStatus === "loading"
      ? "Vardiya verileri bekleniyor"
      : dataStatus === "unavailable"
        ? "Vardiya verileri alınamadı"
        : connection === "online"
          ? "Bağlı"
          : connection === "offline"
            ? "Bağlantı yok"
            : "Bağlantı kontrol ediliyor";
  return (
    <header
      className={`operator-tablet-bar${machineControls ? " has-machine-controls" : ""}`}
    >
      <div className="operator-terminal-identity">
        <strong>
          {label}
          <span
            className={`operator-header-link is-${connection}`}
            role="status"
            aria-label={connectionText}
            title={connectionText}
          >
            {connection === "online" ? (
              <Wifi size={18} aria-hidden="true" />
            ) : connection === "offline" ? (
              <WifiOff size={18} aria-hidden="true" />
            ) : (
              <LoaderCircle size={18} aria-hidden="true" />
            )}
          </span>
        </strong>
        {terminalCode ? (
          <small title={terminalName ?? "Tablet"}>
            <code>{terminalCode}</code>
          </small>
        ) : null}
      </div>
      <span className="operator-header-clock" title={`Vardiya: ${hours}`}>
        <time dateTime={clock.toISOString()} aria-label="Güncel saat">
          {clock.toLocaleTimeString("tr-TR", {
            timeZone: "Europe/Istanbul",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          })}
        </time>
        {operatingDay ? (
          <time
            className="operator-header-date"
            dateTime={operatingDay.date}
            aria-label="Operasyon günü"
          >
            {new Intl.DateTimeFormat("tr-TR", {
              day: "numeric",
              month: "long",
              year: "numeric",
              timeZone: "Europe/Istanbul",
            }).format(new Date(`${operatingDay.date}T12:00:00+03:00`))}
          </time>
        ) : null}
      </span>
      <span className="operator-identity">
        {operatingDay ? (
          <button
            className="operator-header-completed"
            type="button"
            onClick={operatingDay.onCompleted}
            aria-haspopup="dialog"
            aria-label={`Tamamlanan işler (${operatingDay.completedCount})`}
          >
            <CheckCircle2 size={19} aria-hidden="true" />
            <span>Tamamlanan</span>
            <b>{operatingDay.completedCount}</b>
          </button>
        ) : null}
        {machineControls}
        {machineControls ? (
          <OperatorHeaderMenu
            name={displayName ?? user.name}
            connection={connectionText}
            onLock={onLock}
            onActions={onActions}
            onLogout={onLogout}
          />
        ) : (
          <>
            <strong>{displayName ?? user.name}</strong>
            {onLock ? (
              <button
                type="button"
                aria-label="Ekranı kilitle"
                onClick={() => void onLock()}
              >
                <LockKeyhole size={19} />
              </button>
            ) : null}
            {onActions ? (
              <button
                type="button"
                aria-label="İş emri ve operatör işlemleri"
                onClick={onActions}
              >
                <GitBranch size={19} />
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Çıkış yap"
              onClick={() => void onLogout()}
            >
              <LogOut size={19} />
            </button>
          </>
        )}
      </span>
    </header>
  );
}
